import api from '@pray/shared/utils/api';
import type { ListResponse, ObjectResponse } from '@pray/shared/utils/api/types/response';

// PRAY-staff API behind the SF Weekly Report page (Studio › Admin tools). Every call needs the
// Studio admin grant; server_pray's /studio/superfunnel/weekly-report/* routes enforce it.

const BASE = '/web/studio/superfunnel/weekly-report';

export type WeeklyReportRecipients = 'owners' | 'owners_else_editors' | 'owners_and_editors';

export type WeeklyReportScope = 'global' | 'organization' | 'artist';

export type WeeklyReportSettingsFields = {
  is_enabled: boolean;
  recipients: WeeklyReportRecipients;
  // ISO weekday: 1 = Monday … 7 = Sunday.
  send_weekday: number;
  send_hour: number;
  // IANA zone, e.g. America/New_York.
  timezone: string;
};

// In an override every field may be null, meaning "use the organization's or the default value".
export type WeeklyReportOverrideFields = {
  [K in keyof WeeklyReportSettingsFields]: WeeklyReportSettingsFields[K] | null;
};

export type WeeklyReportSettingsEntry<Fields> = Fields & {
  scope_type: WeeklyReportScope;
  // Organizations.id / Artists.id; null for the defaults.
  scope_id: string | null;
  scope_name: string | null;
  updated_at: string;
};

export type WeeklyReportOverrideEntry = WeeklyReportSettingsEntry<WeeklyReportOverrideFields> & {
  // What its null fields fall back to: for a ministry its organization's override, then the defaults.
  // Absent from a server_pray older than this field; fall back to the defaults then.
  inherited?: WeeklyReportSettingsFields;
};

export type WeeklyReportSettingsResponse = {
  defaults: WeeklyReportSettingsEntry<WeeklyReportSettingsFields>;
  overrides: WeeklyReportOverrideEntry[];
};

export type SaveWeeklyReportSettingsBody =
  | ({ scope_type: 'global' } & WeeklyReportSettingsFields)
  | ({ scope_type: 'organization' | 'artist'; scope_id: string } & WeeklyReportOverrideFields);

export type WeeklyReportUnsubscribeEntry = {
  id: string;
  email: string;
  // null means every ministry.
  artist_id: string | null;
  artist_name: string | null;
  source: 'link' | 'admin';
  created_by_name: string | null;
  created_at: string;
};

export type WeeklyReportNextSend = { at: string; label: string };

export type WeeklyReportSummary = {
  next_default_send: WeeklyReportNextSend;
  // Ministries whose SuperFunnel is going this week, whatever their report settings say.
  eligible_ministries: number;
  // Of those, the ones the superfunnel-weekly-report flag is on for.
  in_rollout: number;
  // Of those, the ones with the report on: the ministries the next run would email.
  receiving: number;
  overrides: number;
  unsubscribes: number;
};

export type WeeklyReportPreview = {
  ministry_name: string;
  settings: WeeklyReportSettingsFields;
  // What a field of the ministry's own override falls back to: its organization's, then the defaults.
  inherited_settings?: WeeklyReportSettingsFields;
  next_send: WeeklyReportNextSend;
  report_enabled: boolean;
  superfunnel_going: boolean;
  flag_on: boolean;
  // Someone on the list hasn't unsubscribed; the run skips a ministry with nobody left.
  has_recipients: boolean;
  will_send: boolean;
  recipients: { email: string; role: 'owner' | 'editor'; unsubscribed: boolean }[];
};

// SendGrid's figures for one send, over the days from its send_date to its window_end_date.
export type WeeklyReportSendGridStats = {
  // Handed to SendGrid. Can be lower than `sent`: the notifications service skips addresses that fail its email check.
  requests: number;
  delivered: number;
  bounces: number;
  blocks: number;
  spam_reports: number;
  // Approximate: SendGrid's unique opens per day, added up over the window, so one person opening on two days
  // counts twice. Apple Mail opens every email on arrival.
  unique_opens: number;
};

export type WeeklyReportPerformanceWeek = {
  // Calendar days, YYYY-MM-DD.
  send_date: string;
  // The last day the SendGrid and unsubscribe figures cover: the day before the next send, or today.
  window_end_date: string;
  ministries: number;
  // Emails handed to the notifications queue, one per recipient.
  sent: number;
  // Recipients who pressed the email's button at least once. null for a send from before Oct 20, 2026, whose
  // button opened the Revenue Overview directly, so no click was recorded.
  clicked: number | null;
  // Through the email's unsubscribe link, from send_date to window_end_date.
  unsubscribed: number;
  // null when SendGrid has nothing for the send (sent before tagging began on Oct 19, 2026), or when the
  // response's sendgrid is 'unavailable'.
  sendgrid: WeeklyReportSendGridStats | null;
};

export type WeeklyReportPerformance = {
  // 'unavailable': the SendGrid read failed. Our own counts are still there, and every week's sendgrid is null.
  sendgrid: 'ok' | 'unavailable';
  // At most 8, newest first.
  weeks: WeeklyReportPerformanceWeek[];
};

export const getWeeklyReportSettings = async (): Promise<WeeklyReportSettingsResponse> => {
  const response = await api.executeGetRequest<ObjectResponse<WeeklyReportSettingsResponse>>(`${BASE}/settings`);
  return response.data.data;
};

// Saves the defaults, or one organization's or ministry's override.
export const saveWeeklyReportSettings = async (body: SaveWeeklyReportSettingsBody): Promise<void> => {
  await api.executePutRequest(`${BASE}/settings`, body);
};

export const deleteWeeklyReportOverride = async (
  scopeType: 'organization' | 'artist',
  scopeId: string
): Promise<void> => {
  await api.executeDeleteRequest(`${BASE}/settings/${scopeType}/${scopeId}`);
};

export type GetWeeklyReportUnsubscribesParams = { artistId?: string; search?: string };

export const getWeeklyReportUnsubscribes = async ({
  artistId,
  search,
}: GetWeeklyReportUnsubscribesParams = {}): Promise<WeeklyReportUnsubscribeEntry[]> => {
  const params: Record<string, string> = {};
  if (artistId) params.artist_id = artistId;
  if (search) params.search = search;

  const response = await api.executeGetRequest<ListResponse<WeeklyReportUnsubscribeEntry>>(
    `${BASE}/unsubscribes`,
    params
  );
  return response.data.data;
};

// artistId null stops the report from every ministry. Resolves false when the address was already on the list.
export const addWeeklyReportUnsubscribe = async (email: string, artistId: string | null): Promise<boolean> => {
  const response = await api.executePostRequest<ObjectResponse<{ added: boolean }>>(`${BASE}/unsubscribes`, {
    email,
    artist_id: artistId,
  });
  return response.data.data.added;
};

export const deleteWeeklyReportUnsubscribe = async (unsubscribeId: string): Promise<void> => {
  await api.executeDeleteRequest(`${BASE}/unsubscribes/${unsubscribeId}`);
};

export const getWeeklyReportSummary = async (): Promise<WeeklyReportSummary> => {
  const response = await api.executeGetRequest<ObjectResponse<WeeklyReportSummary>>(`${BASE}/summary`);
  return response.data.data;
};

export const getWeeklyReportPerformance = async (): Promise<WeeklyReportPerformance> => {
  const response = await api.executeGetRequest<ObjectResponse<WeeklyReportPerformance>>(`${BASE}/performance`);
  return response.data.data;
};

export const getWeeklyReportPreview = async (artistId: string): Promise<WeeklyReportPreview> => {
  const response = await api.executeGetRequest<ObjectResponse<WeeklyReportPreview>>(`${BASE}/preview/${artistId}`);
  return response.data.data;
};
